import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { EquipmentId } from "@brewbase/db/types";
import { trpc } from "@/hooks/trpc";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckIcon, PlusIcon } from "lucide-react";
import { useMemo } from "react";
import { format } from "date-fns";

export const Route = createFileRoute(
  "/inventory/equipment/$equipment_id/cleaning/",
)({
  component: CleaningIndexPage,
  params: z.object({
    equipment_id: z.coerce.number().transform((val) => val as EquipmentId),
  }),
});

function CleaningIndexPage() {
  const { equipment_id } = Route.useParams();
  const navigate = useNavigate();

  const { data: cleaningRecords } = trpc.cleaning.records.list.useQuery({
    equipmentId: equipment_id,
  });

  const sortedRecords = useMemo(
    () =>
      [...(cleaningRecords ?? [])].sort((a, b) => {
        const dateA = a.performed_at
          ? new Date(a.performed_at)
          : new Date(a.created_at);
        const dateB = b.performed_at
          ? new Date(b.performed_at)
          : new Date(b.created_at);
        return dateB.getTime() - dateA.getTime();
      }),
    [cleaningRecords],
  );

  return (
    <div className="flex-1 p-6 space-y-4 max-w-2xl">
      <div className="flex justify-between items-center">
        <h2 className="text-sm font-medium">Cleaning Records</h2>
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            navigate({
              to: "/inventory/equipment/$equipment_id/cleaning/new",
              params: { equipment_id },
            })
          }
        >
          <PlusIcon className="h-4 w-4 mr-1" />
          New Record
        </Button>
      </div>

      {sortedRecords.length === 0 ? (
        <p className="text-sm text-muted-foreground">No cleaning records.</p>
      ) : (
        <div className="space-y-2">
          {sortedRecords.map((record) => (
            <button
              key={record.id}
              type="button"
              className="w-full text-left border border-olive-6 p-3 text-sm space-y-1 hover:bg-accent/50 cursor-pointer"
              onClick={() =>
                navigate({
                  to: "/inventory/equipment/$equipment_id/cleaning/$record_id",
                  params: { equipment_id, record_id: record.id },
                })
              }
            >
              <div className="flex justify-between items-start">
                <span className="font-medium">
                  {record.form_name} cleaning
                </span>
                <span className="text-muted-foreground text-xs whitespace-nowrap ml-2">
                  {format(
                    record.performed_at
                      ? new Date(record.performed_at)
                      : new Date(record.created_at),
                    "MMM d, yyyy",
                  )}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {record.completed_at ? (
                  <Badge variant="default" className="gap-1 text-xs">
                    <CheckIcon className="h-3 w-3" />
                    Complete
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="text-xs">
                    In Progress
                  </Badge>
                )}
              </div>
              {record.notes && (
                <p className="text-muted-foreground">{record.notes}</p>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
